import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarProvider,
  SidebarRail,
} from "@/components/ui/sidebar";
import * as React from "react";
import CollapsibleItem from "./collapsible-item/CollapsibleItem";
import Item from "./item/Item";
import CustomSidebarTrigger from "./sidebar-trigger";
import { esVisibleEnElMenu, type NodoDelMenu } from "./utils/permissionUtils";
import { SidebarItemWithItems, sidebarLinks } from "./utils/sidebarLinks";
import { useAuthUser } from "@/context/auth/AuthContext";
import { clinicalSectionTitle } from "@/context/auth/clinicalVertical";
import { hasModule } from "@/context/auth/authz";
import useGetWhitelistStatus from "@/hooks/react-query/BigSuppliers/useGetWhitelistStatus";
import { SubscriptionCard } from "./subscription-card/SubscriptionCard";
import { pos } from "@/platform";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const user = useAuthUser();
  const { data: whitelistStatus } = useGetWhitelistStatus();

  const isBigSupplier = !!whitelistStatus?.isAlreadyBigSupplier;

  // Label de la sección clínica por ESPECIE, no por vocab (#59). La regla (y
  // el fallback a "Veterinaria" mientras identity no exponga el flag) vive en
  // `clinicalVertical.ts`, compartida con las pantallas clínicas.
  const clinicalTitle = clinicalSectionTitle(user);

  // Features de plan de ítems `planFeature` (Chat, tienda online) resueltas por
  // `modules` (entitlement del negocio) — la única fuente tras el reshape del /me
  // (`businessPlanFeatures` se eliminó). Es un chequeo a nivel NEGOCIO (no per-user,
  // eso es `can`). OJO: `ai/chat`/`ecommerce/storefront` hoy NO están en `modules`
  // (features comerciales) → quedan ocultas hasta que el BFF las incluya.
  const hasPlanFeature = (planFeature?: string) => hasModule(user, planFeature);

  // Visibilidad de un ítem/subítem: la regla vive en `esVisibleEnElMenu`
  // (pura, probada aparte), con el corte `ownerOnly` sumado al gate y el de
  // `soloEscritorio` sin la caja offline prendida (la web, o el escritorio con
  // el flag `OFFLINE_ENABLED` apagado).
  const isVisible = (node: NodoDelMenu) =>
    esVisibleEnElMenu(user, node, pos.isAvailable);

  const filteredSidebarLinks = sidebarLinks
    // planFeature sigue como gate aparte (aplica incluso a owners); para ítems
    // ya migrados a `resource`, `can` también lo cubre, así que es redundante-
    // seguro, no conflictivo.
    .filter((item) => hasPlanFeature(item.planFeature))
    .filter((item) => isVisible(item))
    .map((item) => {
      // La sección clínica toma su label de humanHealth (no del vocab, #59).
      const labeled = item.clinicalLabel
        ? { ...item, title: clinicalTitle }
        : item;
      if (!labeled.items) return labeled;
      return {
        ...labeled,
        items: labeled.items.filter((subItem) => isVisible(subItem)),
      };
    })
    .filter((item) =>
      item.id === "big-supplier-seller" ? isBigSupplier : true
    );

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader className="h-[70px] flex items-center justify-center">
          <CustomSidebarTrigger />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {filteredSidebarLinks.map((item) => {
                if (item.items && item.items.length > 0) {
                  return (
                    <CollapsibleItem
                      item={item as SidebarItemWithItems}
                      key={item.id}
                    />
                  );
                } else {
                  return <Item item={item} key={item.id} />;
                }
              })}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SubscriptionCard />
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>
      <SidebarInset>{props.children}</SidebarInset>
    </SidebarProvider>
  );
}
